import React from 'react';
import classnames from 'classnames';

import type { DefaultProps, Styles, Theme, NestedMap } from './types';

import Element from './Element';
import mapModifiersToDLS from './mapModifiersToDLS';
import modifiersToClassNames from './modifiersToClassNames';

interface Options<Props> {
  name: string;
  defaultProps?: DefaultProps;
  rootClass?: string;
  styles?: Styles;
  mapModifiersToTheme?: (props: Props) => Theme;
  modifiersToDLSMap?: NestedMap;
  disallowCustomComponent?: boolean;
}

export interface MakeAtomProps {
  children?: React.ReactNode;
  Component?: keyof React.ReactHTML | React.ElementType;
  theme?: Theme;
  className?: string;
  forwardedRef?: any;
  qeId?: string;
  id?: string;
}

function removeStyles<P extends object>(props: P) {
  return Object.entries(props).reduce((rest, [k, v]) => {
    if (!(k.startsWith('$') || k === 'theme')) {
      rest[k] = v;
    }
    return rest;
  }, {} as { [k: string]: string });
}

function filterForStyles<P extends object>(props: P) {
  return Object.entries(props).reduce((rest, [k, v]) => {
    if (k.startsWith('$')) rest[k] = v;
    return rest;
  }, {} as { [k: string]: string });
}

/**
 * Creates an Atom component from a config object. Prevent us for duplicating
 * JSX syntax across Atoms, making it easier for creating Atoms and scalable.
 * See more info at the end of this file
 * @param config
 * @param config.name Atom's name.
 * @param config.defaultProps React Default Props object.
 * @param config.rootClass CSS Class that will be applied to the root element.
 * @param config.mapModifiersToTheme fn to map Modifiers to Theme.
 * @param config.modifiersToDLSMap fn to map Modifiers to DLS class names.
 * @param config.styles Atom's Styles object. Needed for Modifiers.
 * @param config.disallowCustomComponent Ignores the 'Component' prop and only uses the default html element.
 */
function makeAtom<P extends MakeAtomProps>({
  name,
  defaultProps,
  rootClass,
  mapModifiersToTheme,
  modifiersToDLSMap,
  styles,
  disallowCustomComponent,
}: Options<P>): (props: P) => React.ReactElement | null {
  const Alias = (props: P) => {
    const Component =
      (disallowCustomComponent ? defaultProps?.Component : props.Component) ||
      'div';

    const theme = {
      ...((defaultProps && defaultProps.theme) || {}),
      ...(mapModifiersToTheme ? mapModifiersToTheme(props) : {}),
      ...(props.theme || {}),
    };

    return (
      <Element
        {...removeStyles(props)}
        theme={theme}
        className={classnames(
          rootClass,
          modifiersToDLSMap && mapModifiersToDLS(props, modifiersToDLSMap),
          modifiersToClassNames(styles ?? {}, filterForStyles(props)),
          props.className
        )}
        Component={Component}
        forwardedRef={props.forwardedRef}
        qeId={props.qeId ? props.qeId : name}
      />
    );
  };

  Alias.defaultProps = defaultProps;
  Alias.displayName = name;

  return Alias;
}

export default makeAtom;

/*
Why makeAtom?
----------------
This function was created after common patterns were found while creating Atom components.
The goal is to simplify the creation of Atoms by passing an object that acts as a config, and
it does not solve all our problems, meaning that we will have cases where we cannot use `makeAtom`.

What do most of the Atoms have in common?
----------------
1) Ability to customize the HTML Tag (good use case for Element component). This is optional,
   since there are components that require to render a specific HTML Tag.
2) Ability to customize the styling via 'theme' (good use case for Element component).
3) Always have a root Element and CSS Class, e.g. `<button className={styles.root}>...</button>`.
4) Always render the 'children' prop, even if it's undefined. In ReactJS, it's ok to render
   the children component as following: `const Test = ({ children }) => <div>{children}</div>`
   and use it like `<Test />`.
5) Always accept a `className`, e.g. `<Test className="asd" />`.
6) If modifiers are accepted, they will, most of the time, map to a CSS class. This means that
   the modifier $size with value 'lg' should always try to find the cdd class '.size-lg', and
   we can do it via CSS Modules. There's a function helps us called `modifiersToClassNames`,
   that takes the styles object from css modules and the list of modifiers as an object.
7) Some Atoms are wrappers on top of DLS. DLS provides a different CSS Class structure, which
   does not work with our Modifier and Theme system. That means that we need to map modifiers
   to DLS classes. For example, the Button type secondary looks like `<Button $type="secondary" />`,
   and the class name should be `.type_secondary` as part of Shop Components' standard. In DLS,
   the class name is `.btnSecondary`. That means that `$type="secondary"` should use `.btnSecondary`

How does makeAtom work?
----------------
makeAtom is a function that will CREATE a component as soon as the component gets imported,
not every time the component is used. That means that we need things like 'name' (for debugging)
and 'defaultProps'.

It uses the Element component to cover the points 1 and 2. It takes the 'Component' and
'theme' props and passes it directly to Element, which will take care of them. We can
customize the Component by providing it in the `defaultProps`, otherwise it will use 'div'.

Point 1 has a restriction where we should be able to NOT customize the HTML Tag. In that case,
makeAtom accepts a parameter called 'disallowCustomComponent' which will ONLY use the custom
Component if it's provided.

Point 4 and 5 are very common, will always need to be able to take `children` and `className` props
and pass them to Element. Now, `className` is special, because the order of the classes matters,
specially if we're mapping modifiers to classes. The order is [root class, modifiers to dls,
modifiers to our classes, className provided by the dev consuming the component].

Point 6: Atoms are the smallest components that we have. That means that most of the modifiers
will make styling changes rather than structural (html) changes. makeAtom will automatically
find the modifiers from props and try to match them with our css classes.

Point 7 is only needed if the Atom is a wrapper on top of DLS. We should also be able to
map modifiers (maybe the same ones) to our own css classes, in case we need to extend
some functionalities from DLS. The Button component, for example, uses DLS and extends it
via the $size modifier since the DLS version only accepts one size.
*/
